import { ApiError, matricesApi } from '@/lib/api';
import type {
  MatrixGenerationPrompt,
  RegenerationPromptRecord,
  RegenerationScope,
} from '@mss_creator/shared';
import { Alert, Button } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * The Cadrage tab's Prompt section (issues #3 and #6) — the exact text each
 * past run handed to the model, read back from `matrix_generation_jobs` for a
 * full generation and from `regeneration_batches` for a regeneration.
 *
 * The distinction this section exists to hold: `GET /composed-prompt`
 * recomposes the prompt from the matrix as it stands *right now*, and is never
 * persisted. Showing that recomposition and labelling it "the prompt that was
 * used" would be false the first time anyone edits the sujet, a palier or a
 * digest — and false about the runner prompt and the JSON contract even
 * before that, since the recomposition never contained either. So a recorded
 * prompt is shown verbatim, and a job that predates the recording is shown as
 * a reconstruction, under a warning that says so in as many words.
 *
 * Regenerations used to be excluded, under a line saying their prompts were
 * not kept. Issue #6 made that line untenable rather than merely incomplete: a
 * `pms_admin` can now rewrite a regeneration prompt by hand in the drawer, so
 * a cell's content is unexplainable without the text that produced it. The
 * batches record theirs, and they appear here in the same chronology — one
 * list, because the question a reader arrives with ("what produced this
 * cell?") does not distinguish the two.
 *
 * pms_admin-only. The section is simply not rendered for other roles (the
 * repo's convention: a backend 403 plus a hidden entry point, no frontend
 * route guard), and `GET /matrices/:id/generation-prompts` enforces the same
 * gate — this component is the courtesy, that route is the rule.
 */

export interface CadragePromptSectionProps {
  matrixId: string;
}

function formatTimestamp(isoDate: string): string {
  return new Date(isoDate).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Character count as a reader scans it — a prompt runs to tens of thousands. */
function formatLength(text: string): string {
  return `${text.length.toLocaleString('fr-FR')} caractères`;
}

/** What each regeneration scope covered, in the drawer's own vocabulary. */
const SCOPE_LABELS: Record<RegenerationScope, string> = {
  matrix: 'toute la matrice',
  activationBlock: "bloc d'activation",
  row: 'une ligne',
  column: 'une colonne',
  cell: 'une cellule',
  multiCell: 'plusieurs cellules',
};

/** One chronology entry — a full generation or a regeneration batch, merged and sorted by `startedAt`. */
type HistoryEntry =
  | { kind: 'generation'; startedAt: string; entry: MatrixGenerationPrompt }
  | { kind: 'regeneration'; startedAt: string; entry: RegenerationPromptRecord };

export function CadragePromptSection({ matrixId }: CadragePromptSectionProps) {
  const promptsQuery = useQuery({
    queryKey: ['prompt-history', matrixId],
    queryFn: () => matricesApi.listPromptHistory(matrixId),
  });

  /**
   * The live recomposition, fetched **only** when a generation turns out to
   * have no recorded prompt. Deliberately not fetched up front: it is the
   * fallback for old jobs, and paying for it on every visit would blur exactly
   * the line this section draws between what was sent and what would be sent.
   *
   * Not offered for a regeneration with no recorded prompt: `/composed-prompt`
   * composes the *initial-generation* prompt, which shares neither the scope
   * prompt nor the coherence context a regeneration is built from. Showing it
   * there would not be a reconstruction, it would be a different document.
   */
  const needsFallback = (promptsQuery.data?.generations ?? []).some(
    (entry) => entry.prompt === null,
  );
  const composedQuery = useQuery({
    queryKey: ['composed-prompt', matrixId, 'cadrage-fallback'],
    queryFn: () => matricesApi.getComposedPrompt(matrixId),
    enabled: needsFallback,
  });

  if (promptsQuery.isLoading) {
    return <p className="text-gray-500 text-small">Chargement des prompts…</p>;
  }

  if (promptsQuery.isError) {
    const forbidden =
      promptsQuery.error instanceof ApiError && promptsQuery.error.code === 'ROLE_FORBIDDEN';
    return (
      <p className="mt-1 text-danger text-small">
        {forbidden
          ? "Vous n'avez pas les droits pour afficher ces prompts."
          : 'Impossible de charger les prompts enregistrés pour le moment.'}
      </p>
    );
  }

  const history: HistoryEntry[] = [
    ...(promptsQuery.data?.generations ?? []).map(
      (entry): HistoryEntry => ({ kind: 'generation', startedAt: entry.startedAt, entry }),
    ),
    ...(promptsQuery.data?.regenerations ?? []).map(
      (entry): HistoryEntry => ({ kind: 'regeneration', startedAt: entry.startedAt, entry }),
    ),
  ].sort((a, b) => b.startedAt.localeCompare(a.startedAt));

  return (
    <div className="space-y-3">
      {history.length === 0 && (
        <p className="text-gray-600 text-small">
          Cette matrice n'a pas encore été générée — aucun prompt n'a donc été envoyé.
        </p>
      )}

      {history.map((item, index) =>
        item.kind === 'generation' ? (
          <PromptCard
            key={item.entry.jobId}
            entry={item.entry}
            // The most recent run is the one being asked about; the rest are
            // history and start folded so the section stays readable.
            defaultOpen={index === 0}
            fallbackPrompt={composedQuery.data?.prompt ?? null}
            isFallbackLoading={composedQuery.isLoading}
          />
        ) : (
          <RegenerationPromptCard
            key={item.entry.batchId}
            entry={item.entry}
            defaultOpen={index === 0}
          />
        ),
      )}
    </div>
  );
}

/** Transient feedback on the Copier button — reset a couple of seconds later. */
type CopyState = 'idle' | 'copied' | 'failed';

const COPY_LABELS: Record<CopyState, string> = {
  idle: 'Copier',
  copied: 'Copié',
  failed: 'Échec',
};

/**
 * `navigator.clipboard` is absent outside a secure context and can reject on a
 * denied permission, so the failure is reported on the button rather than
 * swallowed — a Copier that silently does nothing on a 25 000-character prompt
 * is worse than one that says it failed.
 */
function useCopyButton(text: string | null) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  async function handleCopy() {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    window.setTimeout(() => setCopyState('idle'), 2000);
  }

  return { label: COPY_LABELS[copyState], handleCopy };
}

interface PromptCardProps {
  entry: MatrixGenerationPrompt;
  defaultOpen: boolean;
  /** The live recomposition, used only when `entry.prompt` is null. */
  fallbackPrompt: string | null;
  isFallbackLoading: boolean;
}

function PromptCard({ entry, defaultOpen, fallbackPrompt, isFallbackLoading }: PromptCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const recorded = entry.prompt !== null;
  const shownText = entry.prompt ?? fallbackPrompt;
  const { label: copyLabel, handleCopy } = useCopyButton(shownText);

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <div className="flex flex-wrap items-center gap-3 border-gray-200 border-b bg-gray-50 px-4 py-2">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          icon={isOpen ? 'expand_more' : 'chevron_right'}
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
        >
          Génération {entry.status === 'failed' ? 'échouée' : 'initiale'}
        </Button>
        <span className="text-gray-600 text-small">{formatTimestamp(entry.startedAt)}</span>

        {recorded && entry.runnerPromptName && (
          <span className="rounded-md bg-gray-100 px-2 py-0 text-caption text-gray-600">
            {entry.runnerPromptName}
            {entry.runnerPromptVersion === null ? '' : ` v${entry.runnerPromptVersion}`}
          </span>
        )}
        {entry.usedPromptOverride && (
          <span className="rounded-md bg-bleu-nuit-tint px-2 py-0 font-semibold text-bleu-nuit text-caption">
            Prompt modifié à la main
          </span>
        )}

        <div className="flex-1" />

        <span className="text-caption text-gray-500 tabular-nums">
          {recorded && entry.prompt ? formatLength(entry.prompt) : 'non enregistré'}
        </span>
        {isOpen && shownText && (
          <Button
            type="button"
            variant="primary"
            size="xs"
            icon="content_copy"
            onClick={() => void handleCopy()}
          >
            {copyLabel}
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="space-y-3 p-4">
          {!recorded && (
            <div data-testid="prompt-reconstitue">
              <Alert tone="warning" title="Prompt reconstitué.">
                Cette génération est antérieure à l'enregistrement des prompts. Le texte affiché est
                recomposé à partir de l'état actuel de la matrice et peut différer de ce qui a
                réellement été envoyé.
              </Alert>
            </div>
          )}

          {shownText === null ? (
            <p className="text-gray-500 text-small">
              {isFallbackLoading ? 'Recomposition du prompt…' : 'Prompt indisponible.'}
            </p>
          ) : (
            // Bounded height with its own scroller: a composed prompt is
            // thousands of lines, and letting it set the page's height would
            // bury every other section under it.
            <pre
              data-testid="cadrage-prompt-text"
              className="max-h-112 overflow-auto whitespace-pre-wrap rounded-md bg-white font-mono text-caption text-gray-800 leading-relaxed"
            >
              {shownText}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

interface RegenerationPromptCardProps {
  entry: RegenerationPromptRecord;
  defaultOpen: boolean;
}

/**
 * One regeneration batch (issue #6).
 *
 * No "reconstitué" fallback, unlike its generation sibling: nothing recomposes
 * a past regeneration prompt. `/composed-prompt`'s GET returns the
 * initial-generation composition, which carries neither the scope prompt nor
 * the coherence context, so offering it here would show a different document
 * under this batch's heading. A batch predating the column says it recorded
 * nothing, and stops there.
 */
function RegenerationPromptCard({ entry, defaultOpen }: RegenerationPromptCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { label: copyLabel, handleCopy } = useCopyButton(entry.prompt);

  return (
    <div className="overflow-hidden rounded-md border border-gray-200">
      <div className="flex flex-wrap items-center gap-3 border-gray-200 border-b bg-gray-50 px-4 py-2">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          icon={isOpen ? 'expand_more' : 'chevron_right'}
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
        >
          {entry.status === 'failed' ? 'Régénération échouée' : 'Régénération'} —{' '}
          {SCOPE_LABELS[entry.scope]}
        </Button>
        <span className="text-gray-600 text-small">{formatTimestamp(entry.startedAt)}</span>

        {entry.scopePromptName && (
          <span className="rounded-md bg-gray-100 px-2 py-0 text-caption text-gray-600">
            {entry.scopePromptName}
            {entry.scopePromptVersion === null ? '' : ` v${entry.scopePromptVersion}`}
          </span>
        )}
        {entry.promptEdited && (
          <span
            data-testid="regen-prompt-edited-badge"
            className="rounded-md bg-bleu-nuit-tint px-2 py-0 font-semibold text-bleu-nuit text-caption"
          >
            Prompt modifié à la main
          </span>
        )}

        <div className="flex-1" />

        <span className="text-caption text-gray-500 tabular-nums">
          {entry.prompt ? formatLength(entry.prompt) : 'non enregistré'}
        </span>
        {isOpen && entry.prompt && (
          <Button
            type="button"
            variant="primary"
            size="xs"
            icon="content_copy"
            onClick={() => void handleCopy()}
          >
            {copyLabel}
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="space-y-3 p-4">
          {entry.comment && (
            <p className="text-gray-600 text-small">
              <span className="font-semibold">Commentaire :</span> {entry.comment}
            </p>
          )}
          {entry.prompt === null ? (
            <p className="text-gray-500 text-small">
              Prompt non enregistré — cette régénération est antérieure à leur enregistrement.
            </p>
          ) : (
            <pre
              data-testid="cadrage-regen-prompt-text"
              className="max-h-112 overflow-auto whitespace-pre-wrap rounded-md bg-white font-mono text-caption text-gray-800 leading-relaxed"
            >
              {entry.prompt}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
